import React from 'react';
import { FaCircleCheck, FaArrowUpFromBracket, FaLock } from 'react-icons/fa6';
import Badge from '@/components/Badges';
import { providerPresentation, providerInitials, usesOAuth } from './providerCatalog';

const Tile = ({ onClick, children, connected }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group relative flex flex-col h-full text-left p-5 rounded-xl border transition-all focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
      connected
        ? 'border-emerald-400 dark:border-emerald-600 bg-emerald-50/50 dark:bg-emerald-900/10'
        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-theme-surface-accent hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-md'
    }`}
  >
    {children}
  </button>
);

const ProviderMark = ({ descriptor, logo }) =>
  logo ? (
    <img src={logo} alt="" aria-hidden="true" className="h-10 w-10 object-contain" />
  ) : (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-sm font-semibold text-slate-600 dark:text-slate-300">
      {providerInitials(descriptor)}
    </div>
  );

/**
 * A grid of provider tiles.
 *
 * Used for both integration generations, which differ in where the list comes
 * from rather than how a tile looks: v2 is a hardcoded set with bespoke OAuth
 * handshakes, v1 is whatever menu-service's catalogue returns.
 */
const ProviderTiles = ({ providers, connectedProvider, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {providers.map((descriptor) => {
      const key = descriptor.provider;
      const presentation = providerPresentation(descriptor);
      const connected = connectedProvider === key;

      return (
        <Tile key={key} onClick={() => onSelect(descriptor)} connected={connected}>
          <div className="flex items-start justify-between gap-3">
            <ProviderMark descriptor={descriptor} logo={presentation.logo} />
            {connected ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <FaCircleCheck className="w-3.5 h-3.5" />
                Connected
              </span>
            ) : (
              usesOAuth(descriptor) && (
                <Badge color="blue" size="xs" slim>
                  <span className="inline-flex items-center gap-1">
                    <FaLock className="w-2.5 h-2.5" />
                    OAuth
                  </span>
                </Badge>
              )
            )}
          </div>

          <h3 className="mt-3 text-base font-semibold text-slate-800 dark:text-slate-100">
            {presentation.name}
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 flex-1">
            {presentation.blurb ||
              (usesOAuth(descriptor)
                ? 'Authorize with the provider to connect.'
                : 'Enter your API details to connect.')}
          </p>

          <span className="mt-3 text-sm font-medium text-indigo-600 dark:text-indigo-400 group-hover:underline">
            {connected ? 'Manage' : 'Connect'} →
          </span>
        </Tile>
      );
    })}
  </div>
);

export const SftpTile = ({ onClick, connected = false, host = null }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    <Tile onClick={onClick} connected={connected}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
          <FaArrowUpFromBracket className="w-4 h-4" />
        </div>
        {connected && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <FaCircleCheck className="w-3.5 h-3.5" />
            Configured
          </span>
        )}
      </div>
      <h3 className="mt-3 text-base font-semibold text-slate-800 dark:text-slate-100">
        SFTP Upload
      </h3>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 flex-1">
        {connected ? (
          <>
            Feeding from <span className="font-mono break-all">{host}</span>.
          </>
        ) : (
          <>Scheduled file feeds, for when a direct POS integration isn&rsquo;t available.</>
        )}
      </p>
      <span className="mt-3 text-sm font-medium text-indigo-600 dark:text-indigo-400 group-hover:underline">
        {connected ? 'Manage' : 'Set up'} →
      </span>
    </Tile>
  </div>
);

export default ProviderTiles;
